<template>
  <div :class="[isHeader ? 'isHeader' : '']" class="cart-info-container">
    <v-alert
      v-if="isUserGuest"
      type="info"
      variant="tonal"
      density="compact"
      class="mb-3"
      icon="mdi-briefcase-outline"
    >
      {{ $t("CartSummary.guestQuoteInfo") }}
    </v-alert>
    <div v-if="!isUserGuest">
      <h4>{{ $t("CartSummary.title") }}</h4>
      <div class="cart-info">
        <span class="title">{{ $t("CartSummary.units") }}</span>
        <span class="number">{{ totalUnits }}</span>
      </div>

      <div class="cart-info">
        <span class="title">{{ $t("CartSummary.packages") }}</span>
        <span class="number">{{ totalPackages }}</span>
      </div>

      <v-divider class="my-4"></v-divider>
      <div class="cart-info">
        <span class="title">{{ $t("CartSummary.priceBeforeVAT") }}</span>
        <span class="number highlithed">
          <span style="font-size: 16px">₪</span
          >{{ formattedPrice(priceBeforeVAT) }}
        </span>
      </div>
      <div v-if="totalSavings > 0" class="cart-info savings">
        <span class="title">{{
          $t("CartSummary.totalSavings") + ` (${cartData.discount}%)`
        }}</span>
        <span class="number savings-amount">
          <span style="font-size: 16px">₪</span
          >{{ formattedPrice(totalSavings) }}
        </span>
      </div>
      <div v-if="totalSavings > 0" class="cart-info">
        <span class="title">{{ $t("CartSummary.priceBeforeSavings") }}</span>
        <span class="number highlithed">
          <span style="font-size: 16px">₪</span
          >{{ formattedPrice(priceBeforeVAT - totalSavings) }}
        </span>
      </div>
      <div class="cart-info">
        <span class="title"
          >{{ $t("CartSummary.vat") }} {{ getVat * 100 }}%</span
        >
        <span class="number highlithed">
          <span style="font-size: 16px">₪</span>{{ formattedPrice(vatAmount) }}
        </span>
      </div>
      <div v-if="hasActiveShippingTiers" class="cart-info">
        <span class="title">{{ $t("CartSummary.shipping") }}</span>
        <span class="number highlithed">
          <template v-if="shippingCost > 0">
            <span style="font-size: 16px">₪</span
            >{{ formattedPrice(shippingCost) }}
          </template>
          <template v-else>{{ $t("CartSummary.freeShipping") }}</template>
        </span>
      </div>
      <div class="cart-info sum">
        <h3 class="title">{{ $t("CartSummary.totalPayment") }}</h3>
        <span class="number highlithed">
          <span style="font-size: 16px">₪</span>
          <span class="sum-number">{{ formattedPrice(totalAmount) }}</span>
        </span>
      </div>
    </div>

    <v-btn
      v-if="!isHeader && !isCheckOut"
      :disabled="totalUnits === 0"
      class="endOrder"
      height="50"
      color="#BD9478"
      @click="gotoCheckOut"
    >
      {{
        isUserGuest
          ? $t("CartSummary.guestFinishOrder")
          : $t("CartSummary.finishOrder")
      }}
    </v-btn>
    <QuotationModal
      @closeModel="closeQuotationModal"
      v-if="isQuotationModalOpen"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { formatPrice } from "../../utilities";
import QuotationModal from "./Cart/QuotationModal.vue";
export default {
  name: "CartSummary",
  components: {
    QuotationModal,
  },
  props: {
    isHeader: {
      type: Boolean,
      default: false,
    },
    isCheckOut: {
      type: Boolean,
      default: false,
    },
    cartData: {
      type: Object,
      default: () => ({
        units: 0,
        packages: 0,
        priceBeforeVAT: 0,
        totalpriceSavings: 0,
        discount: 0,
      }),
    },
  },
  data() {
    return {
      isQuotationModalOpen: false,
    };
  },
  methods: {
    closeQuotationModal() {
      this.isQuotationModalOpen = false;
    },
    gotoCheckOut() {
      if (this.isUserGuest) {
        this.isQuotationModalOpen = true;
        return;
      }
      this.$store.dispatch("Orders/setCartModel", false);
      this.$router.push({ name: "checkOut" });
    },
    formattedPrice(price) {
      return formatPrice(price);
    },
  },
  computed: {
    ...mapGetters({
      getVat: "Orders/getTaxPercent",
      getShippingCost: "Orders/getShippingCost",
      getShippingTiers: "Orders/getShippingTiers",
      isUserGuest: "AuthModule/isUserGuest",
      isShippingEnabled: "isShippingEnabled",
    }),
    shippingCost() {
      return this.getShippingCost;
    },
    hasActiveShippingTiers() {
      return (
        this.isShippingEnabled &&
        (this.getShippingTiers || []).some((tier) => tier.active)
      );
    },
    totalUnits() {
      return this.cartData.units;
    },
    totalPackages() {
      return this.cartData.packages;
    },
    priceBeforeVAT() {
      return this.cartData.priceBeforeVAT;
    },
    priceWithDiscount() {
      return (
        this.priceBeforeVAT -
        this.priceBeforeVAT * (this.cartData.discount / 100)
      );
    },
    vatAmount() {
      const priceForVat = this.isCheckOut
        ? this.priceWithDiscount
        : this.priceBeforeVAT;
      // Shipping is VAT-able (Israeli rule), so include it in the VAT base.
      // getVat is already 0 for VAT-free customers.
      return (priceForVat + this.shippingCost) * this.getVat;
    },
    totalAmount() {
      const basePrice = this.isCheckOut
        ? this.priceWithDiscount
        : this.priceBeforeVAT;
      return basePrice + this.vatAmount + this.shippingCost;
    },
    priceBeforeSavings() {
      return this.cartData.priceBeforeVAT + this.totalSavings;
    },
    totalSavings() {
      return this.cartData.totalpriceSavings || 0;
    },
  },
};
</script>

<style scoped lang="scss">
.cart-info-container {
  display: flex;
  padding: 20px;
  background: #f7f7f7;
  flex-direction: column;
  position: relative;
  border-radius: 10px;
  margin-bottom: 80px;
  // gap: 14px;
  h4 {
    font-size: 20px;
  }
  &.isHeader {
    margin-bottom: 60px;
  }
}

.sum {
  margin-top: 10px;
}
.cart-info {
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  h3 {
    font-size: 22px;
  }

  .number {
    font-size: 18px;
    font-weight: bold;
  }

  .sum-number {
    font-size: 30px;
  }
}
.isLaptop {
  .cart-info-container {
    margin-bottom: 80px;
  }
}
.isTablet,
.isMobile {
  .cart-info-container {
    margin-bottom: 80px;
  }
}
</style>
